import classNames from "classnames";
import Container, { FullWidthContainer, WideContainer } from "components/Container";
import Footer from "components/Footer";
import Main from "components/Main";
import Newsletter from "components/Newsletter";
import Image from "next/image";
import CodeBlock from "pages/blog/article/components/CodeBlock";
import ImageCaption from "pages/blog/article/components/ImageCaption";
import Navigation from "./components/Navigation";
import i18n from "i18n";
import Head from "next/head";
import Author from "pages/blog/article/components/Author";
import { client } from "prismic-configuration";
import Prismic from "prismic-javascript";
import React from "react";
import { Document } from "@prismicio/client/types/documents";
import { RichText } from "prismic-reactjs";
import htmlSerializer from "lib/htmlSerializer";

export async function getStaticPaths() {
  const { results: articles } = await client.query(Prismic.Predicates.at("document.type", "article"));

  const paths = articles.map((article: Document) => {
    return {
      params: {
        slug: article.uid,
      },
    };
  });

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps({ params, preview = false }: { params: any; preview: boolean }) {
  const { slug } = params;
  const article = await client.getByUID("article", slug, {
    lang: "en-us",
    fetchLinks: ["author.name", "author.photo"],
  });

  return {
    props: { article },
  };
}

export default function ArticlePage({ article }: { article: Document }) {
  return (
    <>
      <Head>
        <title>
          {article.data.title[0].text} - {i18n.t("Bjarn Bronsveld")}
        </title>
        <meta
          property="og:title"
          content={`${article.data.title[0].text} - ${i18n.t("Bjarn Bronsveld")}`}
          key="og_title"
        />
        <meta
          name="twitter:title"
          content={`${article.data.title[0].text} - ${i18n.t("Bjarn Bronsveld")}`}
          key="twitter_title"
        />
        <meta name="twitter:description" content={article.data.summary} key="twitter_description" />
        <meta name="description" content={article.data.summary} key="description" />
        <meta property="og:description" content={article.data.summary} key="og_description" />
        <meta property="og:type" content="article" key="og_type" />
      </Head>

      <Navigation />

      <Main className="flex flex-col flex-1 pt-20">
        {article.data.cover_image?.url && (
          <FullWidthContainer>
            <div className="relative flex aspect-h-1 aspect-w-2 md:aspect-h-3 md:aspect-w-8 3xl:aspect-h-1 3xl:aspect-w-4 4xl:aspect-h-1 4xl:aspect-w-6">
              <Image
                src={article.data.cover_image.url}
                alt={article.data.cover_image.alt || article.data.title[0].text}
                layout="fill"
                objectFit="cover"
              />
            </div>
          </FullWidthContainer>
        )}
        <Container
          className={classNames("", {
            "pt-24": !article.data.cover_image?.url,
            "mt-8": !!article.data.cover_image?.url,
          })}
        >
          <h1 className="pb-2 bg-gradient-to-r from-[#FF512F] to-[#FFC837] text-transparent bg-clip-text text-6xl sm:text-6xl md:text-9xl font-bold tracking-tight">
            {RichText.asText(article.data.title)}
          </h1>

          <Author article={article} author={article.data.author} />

          <article className="mt-4 space-y-8">
            {article.data.body.map((slice: any, index: number) => {
              switch (slice.slice_type) {
                case "text":
                  return (
                    <div>
                      <RichText key={index} render={slice.primary.text} htmlSerializer={htmlSerializer} />
                    </div>
                  );
                case "image":
                  return <ImageCaption key={index} data={slice.primary} />;
                case "code_block":
                  return (
                    <CodeBlock
                      key={index}
                      data={{ snippet: slice.primary.snippet[0].text, language: slice.primary.language }}
                    />
                  );
                default:
                  return null;
              }
            })}
          </article>
        </Container>
        <WideContainer>
          <div className="mt-32">
            <Newsletter />
          </div>
        </WideContainer>
      </Main>
      <div className="mt-32">
        <Footer />
      </div>
    </>
  );
}
